import m, {Vnode} from "mithril";
import DragDropChartBox from "../DragDropChartBox";

type TCoordinates = {x: number, y:number};

export default class DragDropChartPlot {

    public cursorPoint: TCoordinates|null = null
    public readonly self: DragDropChartPlot;

    constructor(
        private readonly container: DragDropChartBox
    ) {
        this.self = this;
    }


    private coordinateToPercentPosition(coordination: number, min: number, max: number): string {
        return ((coordination-min)/(max-min)*100) + '%';
    }

    private cursorToCoordinate(e: PointerEvent|TouchEvent): TCoordinates {
        const {clientX, clientY} = <PointerEvent>((window.TouchEvent && e instanceof TouchEvent) ? e.touches[0] : e);
        const bb = (<HTMLElement>e.currentTarget).getBoundingClientRect();
        const def = this.container.plotDefinition;
        const x = Math.min(Math.max(Math.round((clientX - bb.left) / bb.width * (def.x.max.value - def.x.min.value)) + def.x.min.value, def.x.min.value), def.x.max.value);
        const y = Math.min(Math.max(Math.round((bb.height - clientY + bb.top) / bb.height * (def.y.max.value - def.y.min.value)) + def.y.min.value, def.y.min.value), def.y.max.value);
        return {x, y};
    }

    drop(e: PointerEvent): void {
        const point = this.container.draggedPoint;
        if (point) {
            const coordinates = this.cursorToCoordinate(e);
            point.x = coordinates.x;
            point.y = coordinates.y;
        }
    }


    private viewPoint(coordinates: TCoordinates, node: Vnode): Vnode {
        const bottom = this.coordinateToPercentPosition(coordinates.y, this.container.plotDefinition.y.min.value, this.container.plotDefinition.y.max.value);
        const left = this.coordinateToPercentPosition(coordinates.x, this.container.plotDefinition.x.min.value, this.container.plotDefinition.x.max.value);
        return m('.point', {
            'data-x': coordinates.x,
            'data-y': coordinates.y,
            style: {bottom, left},
        }, node);
    }

    private viewLine(coordinate: number, orientation: 'vertical'|'horizontal', type: 'axis'|'grid'): Vnode {
        const min = orientation === "vertical" ? this.container.plotDefinition.x.min.value : this.container.plotDefinition.y.min.value;
        const max = orientation === "vertical" ? this.container.plotDefinition.x.max.value : this.container.plotDefinition.y.max.value;
        const bottom = orientation === "vertical" ? '0%' : this.coordinateToPercentPosition(coordinate, min, max);
        const left = orientation === "horizontal" ? '0%' : this.coordinateToPercentPosition(coordinate, min, max);
        const height = orientation === "vertical" ? '100%' : '0';
        const width = orientation === "horizontal" ? '100%' : '0';
        return m(`.line.line-${orientation}.line-${type}`, {
            style: {bottom, left, height, width},
        });
    }

    private viewAxis(): Vnode {
        const questionary = this.container.form.questionary;
        const def = this.container.plotDefinition;
        return m('.layer-axis', {}, [
            this.viewLine(0, "vertical", "axis"),
            this.viewLine(0, "horizontal", "axis"),
            this.viewPoint({x: def.x.max.value, y: 0}, m('.maxSymbolX')),
            this.viewPoint({x: 0, y: def.y.max.value}, m('.maxSymbolY')),
            this.viewPoint({x: def.x.max.value, y: 0}, m('.maxLabelX', {}, questionary.renderText(def.x.max.label))),
            this.viewPoint({x: def.x.min.value, y: 0}, m('.minLabelX', {}, questionary.renderText(def.x.min.label))),
            this.viewPoint({x: 0, y: def.y.max.value}, m('.maxLabelY', {}, questionary.renderText(def.y.max.label))),
            this.viewPoint({x: 0, y: def.y.min.value}, m('.minLabelY', {}, questionary.renderText(def.y.min.label))),
        ]);
    }

    private viewGrid(): Vnode|null {
        const step = this.container.plotDefinition.gridStep;
        if (!step) {
            return null;
        }

        const [left, right, bottom, top] = [this.container.plotDefinition.x.min.value, this.container.plotDefinition.x.max.value, this.container.plotDefinition.y.min.value, this.container.plotDefinition.y.max.value];
        const verticalLines = [];
        const horizontalLines = [];

        for (let i=1; i*step <= right; i++) {
            verticalLines.push(i * step);
        }
        for (let i=-1; i*step >= left; i--) {
            verticalLines.push(i * step);
        }
        for (let i=1; i*step <= top; i++) {
            horizontalLines.push(i * step);
        }
        for (let i=-1; i*step >= bottom; i--) {
            horizontalLines.push(i * step);
        }

        const ticks =  this.container.plotDefinition.ticks;
        let padLen = (ticks ? horizontalLines.reduce((p,c)=>Math.max(p,(''+c).length),0) : 0);
        return m('.layer-grid', {}, [
            ...verticalLines.map(x => this.viewLine(x, 'vertical', 'grid')),
            ...horizontalLines.map(y => this.viewLine(y, 'horizontal', 'grid')),
            ...(ticks ? verticalLines.map(x => this.viewPoint({x, y: 0}, m('.tick.tick-x', {}, x))) : []),
            ...(ticks ? horizontalLines.map(y => this.viewPoint({x: 0, y}, m('.tick.tick-y', {}, (''+y).padStart(padLen, ' ')))) : []),
        ]);
    }

    private viewSnap(): Vnode {
        return m('.layer-snap', {}, [
            this.cursorPoint ? this.viewPoint(this.cursorPoint, m('.point-cursor')) : null
        ]);
    }

    private viewPoints(): Vnode {
        const questionary = this.container.form.questionary;

        return m('.layer-points', {},
            this.container.pointDefinitions
                .filter(point => point.isPlaced())
                .map(point => {
                    const dragged = point === this.container.draggedPoint;
                    return this.viewPoint({x:point.x!, y:point.y!},
                        m('.point-inner'+(dragged?'.dragged':''), {
                            title: `${point.label} [ ${point.x} , ${point.y} ]`,
                            onpointerdown: () => this.container.draggedPoint = point,
                            ontouchstart: (e: TouchEvent) => e.preventDefault(),
                            draggable: false,
                        }, [
                            m('img', {
                                src: point.imageUrl,
                                draggable: false,
                            }),
                            m('.coordinates', {}, [
                                m('span', {}, `[ ${point.x} , ${point.y} ]`),
                            ]),
                            m('label', {}, [
                                m('span', {}, m.trust(questionary.renderText(point.label))),
                            ]),
                        ])
                    )
                })
        );
    }

    public view(): Vnode {
        return m('.DragDropChartPlot', {
            onpointermove: (e: PointerEvent) => this.self.cursorPoint = this.cursorToCoordinate(e),
            ontouchmove: (e: PointerEvent) => this.self.cursorPoint = this.cursorToCoordinate(e),
            onpointerleave: () => this.self.cursorPoint = null,
            onpointerup: (e: PointerEvent) => this.drop(e),
        }, [
            this.viewGrid(),
            this.viewAxis(),
            this.viewSnap(),
            this.viewPoints(),
        ])
    }
}